import { getTokens } from '@yodlpay/tokenlists';
import { Address } from 'viem';
import {
  arbitrum,
  base,
  gnosis,
  mainnet,
  optimism,
  polygon,
} from 'wagmi/chains';
import { SupportedChainId } from '../types';
import usdtABI from '../usdt.abi.json';

export const SUPPORTED_CHAINS = [
  mainnet,
  polygon,
  // bsc,
  arbitrum,
  optimism,
  gnosis,
  base,
] as const;

export const SPONSORED_CHAIN_IDS = SUPPORTED_CHAINS.filter(
  (c) => c.id !== mainnet.id, // mainnet is not sponsored to sponsor
).map((c) => c.id);

const SUPPORTED_TOKENS = getTokens();

export const isSupportedSymbol = (symbol: string) => {
  return SUPPORTED_TOKENS.some((token) => token.symbol === symbol);
};

export const hasSupportedChain = <T extends { chainId?: string | number }>(
  params: T,
): params is T & { chainId: number } =>
  params.chainId !== undefined &&
  SUPPORTED_CHAINS.some(({ id }) => id === Number(params.chainId));

export const SUPPORTED_CHAIN_IDS = SUPPORTED_CHAINS.map(
  (chain) => chain.id,
) as SupportedChainId[];

export const DISCORD = 'https://discord.gg/tKHvxh384Y';
export const TWITTER = 'https://twitter.com/yodlpay';

// This will be used to represent the native token on all chains
export const NATIVE_TOKEN_ADDRESS: Address =
  '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';

// The 'approve' function in the USDT contract has no return types, whereas the ERC20 return type is bool
// This results in an error when simulating the tx.
// Ultimately, this doesn't have an impact on the tx, but we don't want errors being raised unnecessarily.
export const CUSTOM_ABI = {
  USDT: {
    1: usdtABI,
  },
} as { [symbol: string]: { [chainId: number]: any } };

// The maximum number of requests we should make to reach a swap amount out that
// satisfies the invoice

export const MIN_RAW_BALANCE = 0.01;

export const MAX_RAW_BALANCE = 1000000;

export const CURRENCY_TO_SYMBOL = {
  EUR: { symbol: '€' },
  USD: { symbol: '$' },
  GBP: { symbol: '£' },
  JPY: { symbol: '¥' },
  CNY: { symbol: '¥' },
  KRW: { symbol: '₩' },
  INR: { symbol: '₹' },
  RUB: { symbol: '₽' },
  TRY: { symbol: '₺' },
  BRL: { symbol: 'R$' },
  CAD: { symbol: 'C$' },
  AUD: { symbol: 'A$' },
  NZD: { symbol: 'NZ$' },
  IDR: { symbol: 'Rp' },
  THB: { symbol: '฿' },
  VND: { symbol: '₫' },
  CHF: { symbol: 'CHF' },
  SGD: { symbol: 'S$' },
  MXN: { symbol: 'Mex$' },
  PHP: { symbol: '₱' },
  PLN: { symbol: 'zł' },
  SEK: { symbol: 'kr' },
  ILS: { symbol: '₪' },
  ZAR: { symbol: 'R' },
  ETH: { symbol: 'ETH', alt: 'Ξ' },
  WETH: { symbol: 'WETH', alt: 'Ξ' },
  USDT: { symbol: '$' },
  USDC: { symbol: '$' },
  EURe: { symbol: '€' },
  DAI: { symbol: '$' },
};

export const FIAT_PRECISION_DECIMALS = 6;
export const TOKEN_DECIMALS = 6;
const INVOICE_AMOUNT_DECIMALS = 2; // 0.01 USD gives invoiceAmount = 1
export const INPUT_TO_INVOICE_MULTIPLIER = 10 ** INVOICE_AMOUNT_DECIMALS;

export * from '@frontend/common/constants/idendity';

export const hasAblyApiKey = !!process.env.NEXT_PUBLIC_ABLY_API_KEY;

export const YODL_ENS_DOMAIN = 'yodl.eth';

// We migrated from me.yodl.preferences to me.yodl, for backwards compatibility
// We will keep the old key for now, but we will eventually remove it
export const PREFERENCES_TEXT_KEY_LEGACY = 'me.yodl.preferences';
export const PREFERENCES_TEXT_KEY = 'me.yodl';
export const COMMUNITY_TEXT_KEY = 'me.yodl.community';
export const YAPP_TYPE_KEY = 'me.yodl.type';

export const YAPP_TYPES = {
  yapp: 'yapp',
  community: 'community',
};

export const ORIGIN = process.env.NEXT_PUBLIC_BASE_URL || 'https://yodl.me';
export const DOMAIN = 'yodl.me';

export * from './preferenceSections';
